import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { isEmpty, isNil } from 'lodash'
import { IconSearch, IconTrash, IconPlus, IconPencil, IconCheck, IconX } from '@tabler/icons-react'

import { BasicModal } from '@/components/layout/modals/BasicModal'
import { Banner } from '@/components/banners/Banner'
import { Button } from '@/components/buttons/Button'
import { Pill } from '@/components/pills/Pill'
import { Loader } from '@/components/loaders/Loader'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { TextInput } from '@/components/forms/TextInput'
import { TextArea } from '@/components/forms/TextArea'
import { toast } from '@/components/toasts/toast.helpers'
import { fetchTaxonomyOptions, saveTaxonomyOptions } from '@/reducers/playbooks/playbook.actions'

import { isOptionGated } from '../helpers/fieldLinks.helpers'

import { buildTaxonomyEdit, CATEGORIZATION_MAX_OPTIONS } from '../helpers'

const MAX_OPTIONS = CATEGORIZATION_MAX_OPTIONS
const MAX_NAME_LENGTH = 60
const NAME_TOO_LONG_MESSAGE = `Options must be ${MAX_NAME_LENGTH} characters or less`
const AT_LIMIT_MESSAGE = `A categorization field can have at most ${MAX_OPTIONS} options`

// Admin-only editor for a categorization field's options. Like the pick_list
// editor it loads the field's taxonomy by uuid and accumulates adds/edits/
// deletes locally before a single "Save changes" overwrites the taxonomy. It
// differs in two ways: the option set is capped at MAX_OPTIONS, and every
// option must carry a description (categorization classifies against the
// description, not just the name), so adds drop straight into edit mode and
// Save stays disabled until every option is described.
//
// `onLinkToDefault` is offered when the options are this configuration's own
// copy of an org default's, so a saved fork can be pointed back at the default.
//
// `appliesToInheritingPlaybooks` is the same relationship seen from the org
// defaults editor, where this taxonomy is the one being inherited: editing is
// allowed, with a note saying how far the change reaches.
//
// `inheritedFromDefault` and `onBreakFromDefault` behave as in
// PickListOptionsModal: a taxonomy the org defaults own is shown as a record
// rather than a form, since saving it here would change the options for every
// playbook inheriting the field, and forking copies them into this
// configuration alone.
export const CategorizationOptionsModal = ({
  show,
  onClose,
  fieldName,
  taxonomyUuid,
  organizationId,
  onApplyForTesting,
  pendingEdit,
  onSaved,
  gatedOperands = [],
  inheritedFromDefault = false,
  onBreakFromDefault,
  onLinkToDefault,
  appliesToInheritingPlaybooks = false,
}) => {
  const dispatch = useDispatch()
  // Stable per-row keys (option names/uuids aren't reliable keys for new rows).
  const clientIdRef = useRef(0)
  const withClientIds = (options) =>
    options.map((option) => {
      clientIdRef.current += 1
      return { ...option, clientId: clientIdRef.current }
    })

  const [root, setRoot] = useState(null)
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [errored, setErrored] = useState(false)
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')
  // The option being edited inline, plus its draft name + description.
  const [editingClientId, setEditingClientId] = useState(null)
  const [editingName, setEditingName] = useState('')
  const [editingDescription, setEditingDescription] = useState('')
  // An edit row takes the caret as it opens, in the field that still needs
  // typing: a created option arrives already named (from the search box) and
  // owing a description, while an existing option is edited from its name.
  const editNameRef = useRef(null)
  const editDescriptionRef = useRef(null)
  const focusDescriptionOnEditRef = useRef(false)

  useEffect(() => {
    if (!show || !taxonomyUuid) {
      return undefined
    }

    let active = true
    setLoading(true)
    setErrored(false)
    setSearch('')
    setEditingClientId(null)
    // Edits already applied for testing are the list the user last left, so
    // they reopen in place of the saved taxonomy rather than being discarded.
    if (pendingEdit) {
      setRoot(pendingEdit.root)
      setOptions(withClientIds(pendingEdit.options))
      setLoading(false)
      return undefined
    }
    dispatch(fetchTaxonomyOptions(taxonomyUuid))
      .then((result) => {
        if (active) {
          setRoot(result.root)
          setOptions(withClientIds(result.options))
        }
      })
      .catch((err) => {
        console.error(err)
        if (active) {
          setErrored(true)
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false)
        }
      })

    return () => {
      active = false
    }
  }, [show, taxonomyUuid])

  useEffect(() => {
    if (isNil(editingClientId)) {
      return
    }
    const target = focusDescriptionOnEditRef.current ? editDescriptionRef : editNameRef
    target.current?.focus()
  }, [editingClientId])

  const handleRemoveOption = (clientId) =>
    setOptions((current) => current.filter((option) => option.clientId !== clientId))

  const handleStartEdit = (option) => {
    focusDescriptionOnEditRef.current = false
    setEditingClientId(option.clientId)
    setEditingName(option.name)
    setEditingDescription(option.description ?? '')
  }

  const handleCancelEdit = () => {
    setEditingClientId(null)
    setEditingName('')
    setEditingDescription('')
  }

  const editNameTooLong = editingName.trim().length > MAX_NAME_LENGTH
  const editInvalid =
    isEmpty(editingName.trim()) || isEmpty(editingDescription.trim()) || editNameTooLong

  // Confirm keeps the option's uuid (reused on save) and writes both fields.
  const handleConfirmEdit = () => {
    if (editInvalid) {
      return
    }
    setOptions((current) =>
      current.map((option) =>
        option.clientId === editingClientId
          ? { ...option, name: editingName.trim(), description: editingDescription.trim() }
          : option
      )
    )
    handleCancelEdit()
  }

  const query = search.trim()
  const queryLower = query.toLowerCase()
  const visibleOptions = isEmpty(queryLower)
    ? options
    : options.filter(
        (option) =>
          option.name.toLowerCase().includes(queryLower) ||
          (option.description ?? '').toLowerCase().includes(queryLower)
      )

  // Offer creation only when the query names something not already in the list.
  const exactMatchExists = options.some((option) => option.name.trim().toLowerCase() === queryLower)
  const atLimit = options.length >= MAX_OPTIONS
  const queryTooLong = query.length > MAX_NAME_LENGTH
  const showCreate = !inheritedFromDefault && !isEmpty(query) && !exactMatchExists

  const handleCreateOption = () => {
    if (queryTooLong || atLimit) {
      return
    }
    clientIdRef.current += 1
    const clientId = clientIdRef.current
    // Prepend so the new option lands at the top, then drop straight into edit
    // mode so the required description gets filled before it can be saved.
    setOptions((current) => [
      { clientId, taxonomy_uuid: null, name: query, description: '' },
      ...current,
    ])
    setSearch('')
    focusDescriptionOnEditRef.current = true
    setEditingClientId(clientId)
    setEditingName(query)
    setEditingDescription('')
  }

  // Categorization classifies against the description, so the option set is only
  // valid once every option has one. Creation is capped, but a taxonomy can load
  // with more than the limit (e.g. created before the cap), so guard save too.
  const missingDescriptions = options.some((option) => isEmpty((option.description ?? '').trim()))
  const overLimit = options.length > MAX_OPTIONS
  const optionsInvalid =
    loading || errored || saving || !isNil(editingClientId) || missingDescriptions || overLimit
  const saveDisabled = optionsInvalid

  // Lift the current (unsaved) edits so the test can derive inline options and a
  // later save can persist them with option uuids preserved.
  const handleApplyForTesting = () => {
    onApplyForTesting(buildTaxonomyEdit(root, options))
    onClose()
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const result = await dispatch(
        saveTaxonomyOptions({
          root,
          organizationId,
          options: options.map((option) => ({
            taxonomy_uuid: option.taxonomy_uuid ?? null,
            name: option.name.trim(),
            description: (option.description ?? '').trim(),
          })),
        })
      )
      setRoot(result.root)
      setOptions(withClientIds(result.options))
      onSaved?.(result)
      toast.success('Options saved')
    } catch (err) {
      console.error(err)
      toast.error('Failed to save options', { cause: err })
    } finally {
      setSaving(false)
    }
  }

  const renderOptionRow = (option) => {
    if (option.clientId === editingClientId) {
      return (
        <li
          key={option.clientId}
          className="categorization-options__row categorization-options__row--editing"
        >
          <div className="categorization-options__edit-fields">
            <TextInput
              ref={editNameRef}
              small
              value={editingName}
              onChange={(event) => setEditingName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  handleConfirmEdit()
                } else if (event.key === 'Escape') {
                  event.preventDefault()
                  handleCancelEdit()
                }
              }}
              placeholder="Option name"
              data-testid="categorization-edit-input"
            />
            <TextArea
              ref={editDescriptionRef}
              rows={2}
              value={editingDescription}
              onChange={(event) => setEditingDescription(event.target.value)}
              onKeyDown={(event) => {
                // Enter stays a newline in the description; only Escape bails.
                if (event.key === 'Escape') {
                  event.preventDefault()
                  handleCancelEdit()
                }
              }}
              placeholder="Describe when this option applies"
              data-testid="categorization-edit-description"
            />
          </div>
          <Button
            basic
            small
            square
            type="button"
            tooltip="Save option"
            disabled={editInvalid}
            disabledTooltip={editNameTooLong ? NAME_TOO_LONG_MESSAGE : 'Add a name and description'}
            onClick={handleConfirmEdit}
            data-testid="categorization-edit-confirm"
          >
            <IconCheck />
          </Button>
          <Button
            basic
            small
            square
            type="button"
            tooltip="Cancel"
            onClick={handleCancelEdit}
            data-testid="categorization-edit-cancel"
          >
            <IconX />
          </Button>
        </li>
      )
    }

    const needsDescription = isEmpty((option.description ?? '').trim())
    // A condition reads this option by its category uuid; deleting it would
    // leave that gate pointing at nothing and block the flow's next save.
    const isGating = isOptionGated(option, gatedOperands)

    return (
      <li key={option.clientId} className="categorization-options__row">
        <div className="categorization-options__text">
          <span className="categorization-options__name">{option.name}</span>
          {needsDescription ? (
            <span className="categorization-options__missing">Description required</span>
          ) : (
            <span className="categorization-options__description">{option.description}</span>
          )}
        </div>
        {isGating && (
          <Pill
            xs
            outlined
            brand
            tooltip="A field's condition reads this option"
            dataTestId={`categorization-gating-${option.clientId}`}
          >
            In use
          </Pill>
        )}
        {!inheritedFromDefault && (
          <>
            <Button
              basic
              small
              square
              type="button"
              tooltip="Edit option"
              onClick={() => handleStartEdit(option)}
              data-testid={`categorization-edit-${option.clientId}`}
            >
              <IconPencil />
            </Button>
            <Button
              basic
              small
              square
              type="button"
              disabled={isGating}
              disabledTooltip="Option is being used to gate another field, remove that gate to remove the option"
              tooltip="Remove option"
              onClick={() => handleRemoveOption(option.clientId)}
              data-testid={`categorization-delete-${option.clientId}`}
            >
              <IconTrash />
            </Button>
          </>
        )}
      </li>
    )
  }

  const renderBody = () => {
    if (loading) {
      return (
        <div className="categorization-options__loader">
          <Loader data-testid="categorization-loader" />
        </div>
      )
    }
    if (errored) {
      return <ErrorMessage content="Failed to load options." />
    }
    return (
      <>
        {onLinkToDefault && (
          <Banner
            dataTestId="categorization-own-copy-banner"
            informative
            header="These options belong to this configuration only"
          >
            <p>
              This configuration keeps its own copy of these options, so edits here stay with it.
              Linking back uses your organization default options again and leaves this copy behind.
            </p>
          </Banner>
        )}
        {appliesToInheritingPlaybooks && (
          <Banner
            dataTestId="categorization-shared-banner"
            caution
            header="These options apply to every playbook that inherits this field"
          >
            <p>
              Saving changes here updates the options everywhere the field is inherited. A playbook
              that has broken its options off the default keeps its own copy.
            </p>
          </Banner>
        )}
        {inheritedFromDefault && (
          <Banner
            dataTestId="categorization-inherited-banner"
            informative
            header="These options are shared with every playbook that inherits this field"
          >
            <p>
              These options come from your organization defaults, so every playbook that inherits
              this field resolves against them. Break from the default to give this configuration
              its own copy.
            </p>
          </Banner>
        )}
        <div className="categorization-options__search">
          <div className="search-input full-width">
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={inheritedFromDefault ? 'Search options' : 'Search or add an option'}
              data-testid="categorization-search"
            />
            <IconSearch />
          </div>
          <span className="categorization-options__count" data-testid="categorization-count">
            {options.length} / {MAX_OPTIONS}
          </span>
        </div>
        <div className="categorization-options__results">
          {isEmpty(options) && !showCreate ? (
            <p className="muted-text">This field has no options yet.</p>
          ) : (
            <ul className="categorization-options__list" data-testid="categorization-options">
              {showCreate && (
                <li className="categorization-options__row" key="__create__">
                  <Button
                    text
                    type="button"
                    className="categorization-options__create"
                    disabled={queryTooLong || atLimit}
                    disabledTooltip={atLimit ? AT_LIMIT_MESSAGE : NAME_TOO_LONG_MESSAGE}
                    onClick={handleCreateOption}
                    data-testid="categorization-create-option"
                  >
                    <IconPlus />
                    Create &quot;{query}&quot;
                  </Button>
                </li>
              )}
              {visibleOptions.map(renderOptionRow)}
            </ul>
          )}
        </div>
      </>
    )
  }

  return (
    <BasicModal
      show={show}
      onClose={onClose}
      title={`"${fieldName}" options`}
      size="tiny"
      scrolling
    >
      <div className="categorization-options">{renderBody()}</div>
      <div className="categorization-options__footer">
        {inheritedFromDefault && onBreakFromDefault && (
          <Button
            primary
            type="button"
            onClick={() => onBreakFromDefault(options)}
            disabled={loading || errored}
            tooltip="Copy these options into this configuration so you can edit them here"
            data-testid="categorization-break-from-default"
          >
            Break From Default
          </Button>
        )}
        {!inheritedFromDefault && !loading && !errored && overLimit && (
          <span
            className="categorization-options__hint"
            data-testid="categorization-over-limit-hint"
          >
            Remove options to get back to {MAX_OPTIONS} or fewer.
          </span>
        )}
        {!inheritedFromDefault && !loading && !errored && !overLimit && missingDescriptions && (
          <span className="categorization-options__hint" data-testid="categorization-save-hint">
            Every option needs a description.
          </span>
        )}
        {!inheritedFromDefault && onLinkToDefault && (
          <Button
            type="button"
            onClick={onLinkToDefault}
            disabled={loading || errored || saving}
            tooltip="Use your organization default options again - this configuration's own copy, and any options only it has, are left behind"
            data-testid="categorization-link-to-default"
          >
            Use Organization Default
          </Button>
        )}
        {!inheritedFromDefault && onApplyForTesting && (
          <Button
            type="button"
            onClick={handleApplyForTesting}
            disabled={optionsInvalid}
            tooltip="Use these options for the next test run without saving them"
            data-testid="categorization-apply-test"
          >
            Apply for testing
          </Button>
        )}
        {!inheritedFromDefault && (
          <Button
            primary
            type="button"
            onClick={handleSave}
            loading={saving}
            disabled={saveDisabled}
            data-testid="categorization-save"
          >
            Save changes
          </Button>
        )}
      </div>
    </BasicModal>
  )
}
